import React, { useEffect, useRef, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { usePwaInstall } from '../lib/usePwaInstall';
import {
  Download, Smartphone, Monitor, CheckCircle2, Share, Plus, MoreVertical,
  Apple, Chrome, MonitorSmartphone, Zap, WifiOff, Home, Loader2, ChevronDown, ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';

function Step({ n, children }) {
  return (
    <div className="flex gap-3">
      <div className="w-7 h-7 rounded-full gradient-brand flex items-center justify-center text-white font-bold text-sm flex-shrink-0">{n}</div>
      <div className="text-sm text-slate-200 leading-relaxed pt-0.5">{children}</div>
    </div>
  );
}

export default function InstallPage() {
  const pwa = usePwaInstall();
  const autoFired = useRef(false);
  const [waiting, setWaiting] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [promptBlocked, setPromptBlocked] = useState(false);

  /*
   * Native-first flow:
   *   1. If the deferred prompt is already available on mount, fire it immediately.
   *   2. If it isn't available yet, wait up to 1.5s for `beforeinstallprompt`,
   *      then fire. Chrome sometimes fires the event a few hundred ms after load.
   *   3. If still unavailable after 1.5s, show the manual fallback.
   *
   * We only auto-fire once per mount — subsequent visits require the user to tap
   * the big "Install now" button so we don't nag.
   */
  useEffect(() => {
    if (pwa.installed || autoFired.current) return;
    autoFired.current = true;
    let timeoutId;

    const fire = async () => {
      if (!pwa.installable) return;
      setWaiting(true);
      const r = await pwa.promptInstall();
      setWaiting(false);
      if (r.outcome === 'accepted') toast.success('Installing Jixxeo AI…');
      else if (r.outcome === 'dismissed') toast.info('Install cancelled — tap again any time.');
      else if (r.outcome === 'unavailable') setPromptBlocked(true);
    };

    if (pwa.installable) {
      fire();
    } else {
      // Give the browser a short window to fire beforeinstallprompt before we fall back.
      timeoutId = setTimeout(() => {
        if (!pwa.installable && !pwa.installed) {
          setPromptBlocked(true);
          setShowManual(true);
        }
      }, 1600);
    }
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When `installable` flips true after mount (because beforeinstallprompt
  // fired a moment after we loaded), auto-fire the prompt the first time.
  useEffect(() => {
    if (pwa.installable && !pwa.installed && !waiting && !promptBlocked && autoFired.current) {
      (async () => {
        setWaiting(true);
        const r = await pwa.promptInstall();
        setWaiting(false);
        if (r.outcome === 'accepted') toast.success('Installing Jixxeo AI…');
        else if (r.outcome === 'unavailable') setPromptBlocked(true);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pwa.installable]);

  const oneTap = async () => {
    if (!pwa.installable) { setShowManual(true); return; }
    setWaiting(true);
    const r = await pwa.promptInstall();
    setWaiting(false);
    if (r.outcome === 'accepted') toast.success('Installing Jixxeo AI…');
    else if (r.outcome === 'dismissed') toast.info('Install cancelled — tap again any time.');
    else if (r.outcome === 'unavailable') { setPromptBlocked(true); setShowManual(true); }
  };

  // Already installed? Stop here with a success state.
  if (pwa.installed) {
    return (
      <div className="max-w-xl mx-auto py-10 px-4">
        <Card data-testid="install-already" className="glass p-8 flex items-center gap-4 border-emerald-500/40">
          <div className="w-14 h-14 rounded-2xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          </div>
          <div>
            <div className="font-display font-black text-xl">Jixxeo AI Installed</div>
            <div className="text-sm text-slate-400 mt-1">You're already using the installed app — enjoy the fullscreen experience 💜</div>
          </div>
        </Card>
      </div>
    );
  }

  const canAuto = pwa.installable && !promptBlocked;

  return (
    <div className="max-w-xl mx-auto space-y-5 py-6 px-4">
      <div className="text-center space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-500/15 border border-violet-500/30 text-violet-300 text-xs font-semibold">
          <Download className="w-3 h-3" /> Install Jixxeo AI
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-black">
          {canAuto ? 'One tap to install' : waiting ? 'Preparing install…' : 'Add to home screen'}
        </h1>
        <p className="text-slate-400 text-sm max-w-md mx-auto">
          {canAuto
            ? 'Your browser supports native install — tap the button below.'
            : 'Install the app for a fullscreen, distraction-free experience with instant launch and offline shell.'}
        </p>
      </div>

      {/* Native install CTA */}
      <Card data-testid="install-cta-card" className="glass p-5 border-violet-500/50">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl gradient-brand flex items-center justify-center flex-shrink-0">
            <Zap className="w-7 h-7 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-black">
              {canAuto ? 'Ready to install' : waiting ? 'Waiting for browser prompt…' : 'Native prompt unavailable'}
            </div>
            <div className="text-xs text-slate-400 mt-0.5">
              {canAuto
                ? 'Tap Install → confirm in the browser dialog.'
                : waiting
                  ? 'If no dialog appears, use the manual steps below.'
                  : 'Your browser didn\'t offer one-tap install. Use the manual steps for your device below.'}
            </div>
          </div>
        </div>
        <Button
          data-testid="one-tap-install-btn"
          onClick={oneTap}
          disabled={waiting}
          className={`w-full mt-4 h-12 text-base font-bold ${canAuto ? 'gradient-brand text-white shadow-lg shadow-violet-500/30' : 'bg-white/5 text-slate-400'}`}
        >
          {waiting
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Waiting for browser…</>
            : canAuto
              ? <><Download className="w-5 h-5 mr-2" /> Install now</>
              : <>Show manual steps</>}
        </Button>
        {canAuto && !waiting && (
          <div className="text-[11px] text-slate-500 text-center mt-2">
            If the prompt doesn't appear, your browser may have paused install prompts for this session.
            <button data-testid="show-manual-toggle" className="underline ml-1 hover:text-slate-300" onClick={() => setShowManual(v => !v)}>
              {showManual ? 'Hide' : 'Show'} manual steps
            </button>
          </div>
        )}
      </Card>

      {/* Benefits (compact) */}
      <section className="grid grid-cols-3 gap-2">
        {[
          { icon: Zap, title: 'Instant launch' },
          { icon: WifiOff, title: 'Works offline' },
          { icon: Home, title: 'Fullscreen' },
        ].map(b => (
          <Card key={b.title} className="glass p-3 text-center">
            <b.icon className="w-5 h-5 text-violet-300 mx-auto" />
            <div className="text-[11px] font-semibold mt-1">{b.title}</div>
          </Card>
        ))}
      </section>

      {/* Manual fallback — collapsed by default when a native prompt is available */}
      {(showManual || (!canAuto && !waiting)) && (
        <Card data-testid="install-manual-steps" className="glass p-5 space-y-5">
          <div className="flex items-center justify-between">
            <div className="font-display font-bold">Manual installation</div>
            {canAuto && (
              <button data-testid="hide-manual-toggle" className="text-xs text-slate-400 flex items-center gap-1 hover:text-slate-200"
                onClick={() => setShowManual(false)}>
                <ChevronUp className="w-3 h-3" /> Hide
              </button>
            )}
          </div>

          {pwa.platform.android && (
            <div data-testid="guide-android" className="space-y-3">
              <div className="flex items-center gap-2 text-emerald-300 font-bold">
                <Chrome className="w-4 h-4" /> Android (Chrome / Edge / Samsung Internet)
              </div>
              <Step n={1}>Tap the <MoreVertical className="inline w-3.5 h-3.5 mx-1 align-text-bottom text-violet-300" /><span className="font-semibold">3-dot menu</span> in the browser top-right.</Step>
              <Step n={2}>Choose <span className="font-semibold">Install app</span> or <span className="font-semibold">Add to Home screen</span>.</Step>
              <Step n={3}>Confirm — Jixxeo AI launches like any other app.</Step>
            </div>
          )}

          {pwa.platform.ios && (
            <div data-testid="guide-ios" className="space-y-3">
              <div className="flex items-center gap-2 text-slate-200 font-bold">
                <Apple className="w-4 h-4" /> iPhone & iPad (Safari only)
              </div>
              <Step n={1}>Open Jixxeo AI in <span className="font-semibold">Safari</span> — not Chrome or in-app browsers.</Step>
              <Step n={2}>Tap <Share className="inline w-3.5 h-3.5 mx-1 align-text-bottom text-cyan-300" /><span className="font-semibold">Share</span> at the bottom of Safari.</Step>
              <Step n={3}>Scroll and tap <Plus className="inline w-3.5 h-3.5 mx-1 align-text-bottom text-violet-300" /><span className="font-semibold">Add to Home Screen</span>.</Step>
              <Step n={4}>Tap <span className="font-semibold">Add</span>.</Step>
            </div>
          )}

          {!pwa.platform.android && !pwa.platform.ios && (
            <div data-testid="guide-desktop" className="space-y-3">
              <div className="flex items-center gap-2 text-cyan-300 font-bold">
                <Monitor className="w-4 h-4" /> Laptop & Desktop
              </div>
              <Step n={1}>Use <span className="font-semibold">Chrome</span>, <span className="font-semibold">Edge</span>, or <span className="font-semibold">Brave</span>.</Step>
              <Step n={2}>Look for the <Download className="inline w-3.5 h-3.5 mx-1 align-text-bottom text-violet-300" /><span className="font-semibold">Install</span> icon in the address bar (right side).</Step>
              <Step n={3}>Click it → <span className="font-semibold">Install</span>. A dedicated Jixxeo AI window will open.</Step>
            </div>
          )}

          <div className="text-[11px] text-slate-500 pt-2 border-t border-white/5">
            Firefox doesn't support PWA install natively — bookmark to the taskbar instead.
          </div>
        </Card>
      )}

      <p className="text-center text-[11px] text-slate-500">
        Zero ads · Doesn't use storage from your photos or messages.
      </p>
    </div>
  );
}
